<script setup>
import { ref, computed } from 'vue'
import StatusPill from '@/components/shell/StatusPill.vue'
import DetailDrawer from '@/components/shell/DetailDrawer.vue'
import Icon from '@/components/shell/Icon.vue'
import { store } from '@/store/appStore.js'
import { SCHEMES, getSchemeMultiplier } from '@/data/mockData.js'

const search = ref(''); const stageFilter = ref('All')
const STAGES_FILTER = ['All','Order Received','Telecaller Validation','Stock Allocation','Warehouse Processing','Dispatch Pending','Dispatched','Invoice Generated','Delivered']
const filtered = computed(() => {
  const q = search.value.trim().toLowerCase()
  return store.data.orders.value.filter(o => (!q||o.orderNo.toLowerCase().includes(q)||o.pharmacyName.toLowerCase().includes(q)||o.productName.toLowerCase().includes(q)) && (stageFilter.value==='All'||o.currentStage===stageFilter.value))
})
const totalValue = computed(() => filtered.value.reduce((s,o)=>s+o.orderValue,0))

// --- Order Booking form ------------------------------------------------
const showBooking = ref(false)
const toast = ref(null)
const notify = msg => { toast.value = msg; setTimeout(() => { toast.value = null }, 3200) }

const blankForm = () => ({ customerId: '', productSku: '', quantity: 50, schemeApplied: SCHEMES[0], expectedDeliveryDate: '2026-08-02' })
const form = ref(blankForm())

const pharmacies = computed(() => store.data.pharmacies.value)
const products = computed(() => store.data.products.value)
// SCHEMES has null repeated 3x by design (weighted random picks for order
// generation), but that would show "No scheme" three times in a dropdown.
const schemeOptions = computed(() => [...new Set(SCHEMES)])
const selectedProduct = computed(() => products.value.find(p => p.sku === form.value.productSku))
const netValue = computed(() => {
  if (!selectedProduct.value || !form.value.quantity) return 0
  return Math.round(form.value.quantity * selectedProduct.value.basePrice * getSchemeMultiplier(form.value.schemeApplied) * 100) / 100
})

// Credit health pill — recomputes live as the pharmacy/quantity/product change.
const creditInfo = computed(() => form.value.customerId ? store.getCreditUsage(form.value.customerId) : null)
const projectedPct = computed(() => {
  if (!creditInfo.value || !creditInfo.value.limit) return 0
  return Math.round(((creditInfo.value.used + netValue.value) / creditInfo.value.limit) * 100)
})
const creditTone = computed(() => {
  const pct = projectedPct.value
  return pct >= 100 ? 'red' : pct >= 80 ? 'yellow' : 'green'
})
const creditBlocked = computed(() => projectedPct.value >= 100 && !(creditInfo.value && creditInfo.value.overridden))

// Smart Up-Sell Nudge — recommend the top fast-moving brand not already selected.
const upsellSuggestion = computed(() => {
  if (!form.value.productSku) return null
  const fastMovers = [...store.data.brandPerformance.value]
    .filter(b => b.movement === 'Fast' && b.sku !== form.value.productSku)
    .sort((a, b) => b.momGrowthPct - a.momGrowthPct)
  return fastMovers[0] || null
})
const upsellProduct = computed(() => upsellSuggestion.value ? products.value.find(p => p.sku === upsellSuggestion.value.sku) : null)

function openBooking() { form.value = blankForm(); showBooking.value = true }

function addUpsellPack() {
  if (!upsellProduct.value || !form.value.customerId) return
  const added = store.createOrder({
    customerId: form.value.customerId,
    productSku: upsellProduct.value.sku,
    quantity: 20,
    schemeApplied: '5% Cash Discount',
    expectedDeliveryDate: form.value.expectedDeliveryDate,
  })
  if (added) notify(`Added ${added.quantity}x ${added.productName} (${added.schemeApplied}) as a companion order — ${added.orderNo}`)
}

function submitOrder() {
  if (!form.value.customerId || !form.value.productSku || !form.value.quantity) return
  if (creditBlocked.value) return
  const created = store.createOrder({ ...form.value })
  if (created) {
    notify(`${created.orderNo} booked for ${created.pharmacyName} — $${created.orderValue.toLocaleString()}`)
    showBooking.value = false
  }
}

function requestOverride() {
  if (!form.value.customerId) return
  store.requestCreditOverride(form.value.customerId)
  notify('Manager credit override granted for this session.')
}
</script>
<template>
  <div>
    <div class="card module-header" style="display:flex;justify-content:space-between;align-items:flex-end;gap:12px;flex-wrap:wrap;">
      <div><span class="eyebrow">Module 3 — Order Management</span><h2>{{ store.data.orders.value.length }} orders booked</h2></div>
      <button class="btn-primary" @click="openBooking">+ New Order</button>
    </div>

    <div v-if="toast" style="position:fixed;bottom:24px;right:24px;background:var(--color-primary-dark);color:#fff;padding:10px 16px;border-radius:8px;font-size:13px;box-shadow:var(--shadow-lg);max-width:340px;z-index:120;">{{ toast }}</div>

    <div class="stat-row">
      <div class="card stat-card"><div class="stat-value">{{ filtered.length }}</div><div class="stat-label">Orders shown</div></div>
      <div class="card stat-card"><div class="stat-value">${{ totalValue.toLocaleString() }}</div><div class="stat-label">Combined value (USD)</div></div>
      <div class="card stat-card"><div class="stat-value">{{ filtered.filter(o=>o.trafficLight==='Green').length }}</div><div class="stat-label">On-track</div></div>
      <div class="card stat-card"><div class="stat-value">{{ filtered.filter(o=>['Red','Orange'].includes(o.trafficLight)).length }}</div><div class="stat-label">Needs attention</div></div>
    </div>
    <div class="card">
      <div class="toolbar">
        <input class="search-input" v-model="search" placeholder="Search order no., pharmacy or product…" />
        <select class="filter-select" v-model="stageFilter"><option v-for="s in STAGES_FILTER" :key="s">{{ s }}</option></select>
        <span class="result-count">{{ filtered.length }} of {{ store.data.orders.value.length }} shown</span>
      </div>
      <div class="table-wrap">
        <table class="data-table">
          <thead><tr><th>Order No.</th><th>Pharmacy</th><th>Product</th><th>Qty</th><th>Scheme</th><th>Value (USD)</th><th>Order Date</th><th>Stage</th><th>Status</th></tr></thead>
          <tbody>
            <tr v-for="o in filtered" :key="o.orderNo">
              <td class="cell-strong">{{ o.orderNo }}</td><td>{{ o.pharmacyName }}</td><td>{{ o.productName }}</td>
              <td class="cell-muted">{{ o.quantity }}</td><td class="cell-muted">{{ o.schemeApplied||'—' }}</td>
              <td class="cell-muted">${{ o.orderValue.toLocaleString() }}</td><td class="cell-muted">{{ o.orderDate }}</td>
              <td class="cell-muted">{{ o.currentStage }}</td><td><StatusPill :status="o.trafficLight" /></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <DetailDrawer :open="showBooking" title="New Order" @close="showBooking=false">
      <div class="form-group">
        <label>Pharmacy</label>
        <select class="form-select" v-model="form.customerId">
          <option value="" disabled>Select a pharmacy…</option>
          <option v-for="p in pharmacies" :key="p.customerId" :value="p.customerId">{{ p.pharmacyName }} — {{ p.territory }}</option>
        </select>
      </div>

      <div v-if="form.customerId && creditInfo" class="oos-remarks" style="margin-bottom:16px;">
        <div style="display:flex;align-items:center;justify-content:space-between;gap:10px;flex-wrap:wrap;">
          <span style="display:flex;align-items:center;gap:6px;font-size:12.5px;">
            <span class="status-pill" :class="creditTone" style="display:inline-flex;"><span class="dot"></span>Credit Limit {{ projectedPct }}% Used</span>
          </span>
          <span class="cell-muted" style="font-size:11.5px;">${{ creditInfo.used.toLocaleString() }} outstanding of ${{ creditInfo.limit.toLocaleString() }} limit</span>
        </div>
        <div v-if="creditBlocked" style="margin-top:10px;">
          <p class="muted" style="font-size:12px;margin:0 0 8px;">This order would push the pharmacy over its credit limit. A manager override is required to proceed.</p>
          <button class="filter-select" style="cursor:pointer;" @click="requestOverride">Request Manager Credit Override</button>
        </div>
        <div v-else-if="creditInfo.overridden" style="margin-top:8px;">
          <span class="cell-muted" style="font-size:11.5px;">✓ Manager override active for this pharmacy this session.</span>
        </div>
      </div>

      <div class="form-group">
        <label>Product / SKU</label>
        <select class="form-select" v-model="form.productSku">
          <option value="" disabled>Select a product…</option>
          <option v-for="p in products" :key="p.sku" :value="p.sku">{{ p.productName }} ({{ p.sku }})</option>
        </select>
      </div>

      <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">
        <div class="form-group">
          <label>Quantity</label>
          <input class="form-input" type="number" min="1" v-model.number="form.quantity" />
        </div>
        <div class="form-group">
          <label>Scheme Applied</label>
          <select class="form-select" v-model="form.schemeApplied">
            <option v-for="s in schemeOptions" :key="s||'none'" :value="s">{{ s || 'No scheme' }}</option>
          </select>
        </div>
      </div>

      <div class="form-group">
        <label>Expected Delivery Date</label>
        <input class="form-input" type="date" v-model="form.expectedDeliveryDate" />
      </div>

      <div v-if="selectedProduct" class="oos-remarks" style="margin-bottom:16px;">
        <strong style="display:block;font-size:11px;text-transform:uppercase;letter-spacing:.04em;margin-bottom:3px;color:var(--color-text-muted);">Net Order Value</strong>
        ${{ netValue.toLocaleString() }}
        <span v-if="form.schemeApplied" class="cell-muted"> (after {{ form.schemeApplied }})</span>
      </div>

      <!-- Smart Up-Sell Nudge Engine -->
      <div v-if="upsellProduct && form.customerId" class="scenario-banner growth" style="margin-bottom:16px;">
        <div>
          <strong>💡 Nudge:</strong> {{ upsellProduct.productName }} ({{ upsellSuggestion.brandName }}) is up {{ upsellSuggestion.momGrowthPct }}% month-over-month — a fast mover worth stocking alongside this order.
          <div style="margin-top:10px;">
            <button class="filter-select" style="cursor:pointer;" @click="addUpsellPack">+ Add Recommended Scheme Pack</button>
          </div>
        </div>
      </div>

      <button class="btn-primary" :disabled="!form.customerId || !form.productSku || !form.quantity || creditBlocked" @click="submitOrder">
        Submit Order
      </button>
      <p v-if="creditBlocked" class="muted" style="font-size:12px;margin-top:8px;">Resolve the credit hold above before submitting.</p>
    </DetailDrawer>
  </div>
</template>